<template>
    <menu-layout :class="[isSmallScreen ? 'small-screen' : '']">
        <router-view
            :key="routerKey"
            v-if="!loading"></router-view>
        <div v-else class="boot-loading-skeleton-container n-col-1 n-flex n-flex-dr-c n-p-6">
            <nskeleton :loading="true" :ani="[
                { width: '40%', height: '40px' },
                { width: '100%', height: '280px' },
                { width: '100%', height: '180px' },
                { width: '70%', height: '120px' }
            ]" />
        </div>
    </menu-layout>

    <div
        v-if="showDragOverlay"
        class="drag-overlay">
        <div class="drag-overlay-content n-flex n-flex-dr-c n-flex-a-c n-flex-j-c">
            <img
                src="@/assets/images/image.png"
                alt="drag-overlay"
                class="drag-overlay-image" />
            <span>{{ $t('chat.dragOverlay') }}</span>
        </div>
    </div>

    <button
        class="test-button"
        v-if="showTestButton"
        @click="showTestPanel = true">
        TEST
    </button>

    <login-box
        :redirect-type="loginRedirectType"
        :default-login-type="defaultLoginType"
        v-if="showLoginBox"
        @close="onLoginBoxClose"
        @loginSuccess="handleLoginSuccess"></login-box>

    <component :is="TestPanel" v-if="TestPanel" :visible="showTestPanel"></component>
</template>
<script setup lang="ts">
import { onMounted, onUnmounted, computed, watch, ref, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';
import { checkGoogleLoginCallback } from '@/utils/googleAuth';
import { checkAppleLoginCallback } from '@/utils/AppleAuth';
import { logEvent } from '@/utils/utils.ts';
import { getAppstoreInfo } from '@/api/app';
import { EVENT_TYPES, globalEmit, on, off } from '@/utils/eventBus';
import { ensureInitialized, promptOneTap, decodeGoogleCredential } from '@/utils/googleOneTap';
import { REQUEST_SUCCESS_CODE } from '@/config/constant';
import { checkUserBase } from '@/utils/user';
import { CHAT_PENDING_DEMO_STORAGE_KEY } from '@/utils/chat';
import { useRoute, useRouter } from 'vue-router';
import { redirectByLogin } from '@/utils/user';
import { checkAuth } from '@/utils/authCheck';
import { registerIframeRoutes } from '@/router';

import config, { PROD_SITE_URL, UAT_SITE_URL } from '@/config';

// 组件
import menuLayout from './components/business/menu-layout.vue';
const TestPanel = import.meta.env.VITE_APP_ENV == 'development' ? defineAsyncComponent(() => import('./components/business/test-panel.vue')) : null;
const LoginBox = defineAsyncComponent(() => import('@/components/business/user/login-box.vue'));

// 数据
const store = useStore();
const route = useRoute();
const router = useRouter();
const defaultLoginType = ref(1);
const loginRedirectType = ref('');
const showLoginBox = ref(false);
const loading = ref(false);
const isSmallScreen = computed(() => store.getters['viewport/isSmallScreen']);
const realTheme = computed(() => store.getters['theme/realTheme']);
const isLoggedIn = computed(() => store.getters['user/isLoggedIn']);
const showTestPanel = ref(false);
const showTestButton = computed(() => config.isDev && route.name !== 'home');
const showDragOverlay = ref(false);
const dragOverlayTimer = ref<number | null>(null);
// router-view 的 key，用于强制重新挂载组件
const routerKey = ref(0);

if (typeof document !== 'undefined') {
    document.body.classList.add(`theme-${realTheme.value}`);
}

// 监听主题变化，给 body 添加主题类
watch(
    realTheme,
    newTheme => {
        if (typeof document === 'undefined') return;
        document.body.classList.remove(`theme-light`);
        document.body.classList.remove(`theme-dark`);
        document.body.classList.add(`theme-${newTheme}`);
    },
    { immediate: true }
);

watch(
    isLoggedIn,
    newIsLoggedIn => {
        if (newIsLoggedIn) {
            // 登录态变为已登录时统一关闭登录框：覆盖「绕过登录框」的登录路径
            // （如 Apple/Google 整页 OAuth 回调在 checkXxxLoginCallback 里直接 dispatch 登录，
            // 不会触发登录框的 loginSuccess 事件，否则自动弹出的登录框无人关闭）。
            showLoginBox.value = false;

            store.dispatch('chat/initDatabase');
            store.dispatch('testprep/initDatabase');
            store.dispatch('video/initDatabase');
            store.dispatch('app/setAppConfig');
        }
    },
    { immediate: true }
);

const fetchAppStoreRating = async () => {
    try {
        const res = await getAppstoreInfo();

        if (res.code == REQUEST_SUCCESS_CODE && res.data.rating) {
            store.commit('app/SET_APP_STORE_RATING', Number(res.data.rating).toFixed(1));
        } else {
            store.commit('app/SET_APP_STORE_RATING', '4.8');
        }
    } catch (error) {
        // Ignore errors (CORS/network) and keep fallback.
    }
};

const isChatRoute = () => route.name === 'chat' || route.name === 'video';

const handleLoginSuccess = (redirectType: string) => {
    showLoginBox.value = false;

    // 游客 AP session 转正：后端已通过 deviceCode 迁移数据，前端去掉 guestMode 即可
    if (redirectType === 'apexam-guest-resume') {
        const guestSession = store.getters['apexam/currentSession'];
        if (guestSession?.guestMode) {
            logEvent('web_ap_guest_login_success');
            store.dispatch('apexam/setCurrentSession', {
                ...guestSession,
                guestMode: false,
                guestStartConfig: undefined
            });
            return;
        }
    }

    redirectByLogin(redirectType);
};

const extractImageFiles = (items: DataTransferItemList | FileList | null) => {
    if (!items) {
        return [];
    }

    const files: File[] = [];

    for (let i = 0; i < items.length; i++) {
        const item = items[i] as any;
        if (!item) continue;

        if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file && file.type.startsWith('image/')) {
                files.push(file);
            }
        } else if (item instanceof File) {
            if (item.type.startsWith('image/')) {
                files.push(item);
            }
        }
    }

    return files;
};

const handlePaste = (event: ClipboardEvent) => {
    if (!isChatRoute()) {
        return;
    }

    if (!checkAuth({ redirectType: route.name === 'video' ? 'video' : 'chat' })) {
        return; // 未登录，已自动打开登录框，直接返回
    }

    const files = extractImageFiles(event.clipboardData?.items || null);

    if (files.length === 0) {
        return;
    }
    event.preventDefault();
    globalEmit(EVENT_TYPES.UPLOAD_FILES, files);
};

const hasFileDrag = (event: DragEvent) => {
    const types = event.dataTransfer?.types;
    return types && Array.from(types).includes('Files');
};

const handleDragEnter = (event: DragEvent) => {
    if (!isChatRoute()) {
        return;
    }

    if (hasFileDrag(event)) {
        showDragOverlay.value = true;
    }
};

const handleDragOver = (event: DragEvent) => {
    if (!isChatRoute()) {
        return;
    }

    event.preventDefault();

    if (!hasFileDrag(event)) {
        return;
    }

    showDragOverlay.value = true;

    if (dragOverlayTimer.value) {
        window.clearTimeout(dragOverlayTimer.value);
    }

    dragOverlayTimer.value = window.setTimeout(() => {
        showDragOverlay.value = false;
        dragOverlayTimer.value = null;
    }, 200);
};

const handleDragLeave = (event: DragEvent) => {
    if (!isChatRoute()) {
        return;
    }

    if (event.relatedTarget === null) {
        showDragOverlay.value = false;
    }
};

const handleDrop = (event: DragEvent) => {
    if (!isChatRoute()) {
        return;
    }

    const files = extractImageFiles(event.dataTransfer?.items || event.dataTransfer?.files || null);

    event.preventDefault();

    if (!checkAuth({ redirectType: route.name === 'video' ? 'video' : 'chat' })) {
        return; // 未登录，已自动打开登录框，直接返回
    }

    showDragOverlay.value = false;

    if (files.length === 0) {
        return;
    }

    showDragOverlay.value = false;

    globalEmit(EVENT_TYPES.UPLOAD_FILES, files);
};

const handleIframeMessage = async (event: MessageEvent) => {
    // event.origin is the only field a sender cannot forge — validate it before trusting any payload.
    // The career page is served from the same public site origin; include both deployment domains plus local dev.
    const allowedOrigins = Array.from(new Set([config.siteUrl.replace(/\/+$/, ''), PROD_SITE_URL, UAT_SITE_URL, window.location.origin]));
    if (!allowedOrigins.includes(event.origin)) return;

    if (event.data && event.data.source === 'sovi_career_iframe') {
        if (event.data.action === 'google_login_success' && event.data.credential) {
            try {
                const googleUserInfo = decodeGoogleCredential(event.data.credential);
                if (googleUserInfo?.email && googleUserInfo?.sub) {
                    await store.dispatch('user/googleLogin', {
                        googleUserInfo: {
                            email: googleUserInfo.email,
                            picture: googleUserInfo.picture || '',
                            name: googleUserInfo.name || googleUserInfo.email,
                            id: googleUserInfo.sub
                        }
                    });
                }
            } catch (error) {
                console.error('Failed to log in from career iframe message:', error);
            }
        }
    }
};

const onGoLogin = (data: any) => {
    // 非 welcome demo 触发的登录：清掉「登录后继续发 demo」的暂存，避免 OAuth 跳走 / 改 URL 后再登录出现幽灵发送
    if (!data?.pendingChatDemo && typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(CHAT_PENDING_DEMO_STORAGE_KEY);
    }
    defaultLoginType.value = data.loginType;
    loginRedirectType.value = data.type;
    showLoginBox.value = true;
};

// 同步注册，确保在子路由组件 onMounted（如 UserHome 未登录自动弹登录框）之前监听已就绪
on(EVENT_TYPES.GO_LOGIN, onGoLogin);

// 未登录访问用户首页(/home, name=user-home)时自动打开登录弹框（在 App 内处理，避免子组件事件时序问题）
watch(
    () => route.name,
    name => {
        if (name === 'user-home' && !isLoggedIn.value) {
            onGoLogin({ loginType: 1, type: 'home' });
        }
    },
    { immediate: true }
);

const onLoginBoxClose = () => {
    showLoginBox.value = false;
    if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(CHAT_PENDING_DEMO_STORAGE_KEY);
    }
};

const onLoginSuccess = (payload: unknown) => {
    handleLoginSuccess(typeof payload === 'string' ? payload : '');
};

const onReloadRouter = () => {
    // 通过改变 key 强制重新挂载 router-view，触发组件的 onMounted
    routerKey.value++;
};

onMounted(async () => {
    console.log('App mounted');

    // 在浏览器空闲时执行非关键任务，避免与首屏关键渲染争抢主线程（改善 LCP/TBT/INP）
    const runWhenIdle = (cb: () => void) => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(cb, { timeout: 2000 });
        } else {
            window.setTimeout(cb, 200);
        }
    };

    // 最先加载菜单本地缓存，sidebar 立即可渲染（同步，无需等待）
    store.dispatch('menu/loadLocal');

    if (router.currentRoute.value.meta.requireAuth) {
        loading.value = true;
    }

    const [appleLoginSuccess, googleLoginSuccess] = await Promise.all([checkAppleLoginCallback(), checkGoogleLoginCallback()]);

    store.dispatch('user/cacheGradeList');

    await store.dispatch('theme/initTheme');
    await store.dispatch('viewport/initViewport');

    if (!appleLoginSuccess && !googleLoginSuccess) {
        await checkUserBase();
    }

    // 登录态相关的数据库/配置初始化由上方 watch(isLoggedIn, { immediate: true }) 统一处理，
    // 此处不再重复 await，避免阻塞首屏关键渲染路径。
    loading.value = false;

    // 异步从服务端同步最新菜单 + 注册 iframe 路由（不阻塞页面展示）
    store.dispatch('menu/syncRemote').then(() => {
        const iframeMenus = store.getters['menu/iframeMenus'];
        if (iframeMenus.length > 0) {
            registerIframeRoutes(router, iframeMenus);
        }
    });

    logEvent('web_launch_feature_show', {
        page_name: router.currentRoute.value.name || router.currentRoute.value.path
    });

    runWhenIdle(() => fetchAppStoreRating());

    window.addEventListener('paste', handlePaste);
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('message', handleIframeMessage);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    on(EVENT_TYPES.RELOAD_ROUTER, onReloadRouter);
    on(EVENT_TYPES.LOGIN_SUCCESS, onLoginSuccess);

    // One Tap：首页 / chat / study / apexam 且未登录时展示 Google One Tap（空闲时初始化，避免阻塞首屏）
    runWhenIdle(() => {
        const oneTapRoutes: Record<string, string> = {
            home: '',
            chat: 'chat',
            study: 'study',
            apexam: 'apexam'
        };
        const routeName = route.name as string;
        const redirectType = oneTapRoutes[routeName];
        if (redirectType !== undefined && !store.getters['user/isLoggedIn']) {
            ensureInitialized()
                .then(() => promptOneTap(redirectType))
                .catch(() => {});
        }
    });
});

onUnmounted(() => {
    window.removeEventListener('paste', handlePaste);
    window.removeEventListener('dragenter', handleDragEnter);
    window.removeEventListener('message', handleIframeMessage);
    window.removeEventListener('dragover', handleDragOver);
    window.removeEventListener('dragleave', handleDragLeave);
    window.removeEventListener('drop', handleDrop);

    if (dragOverlayTimer.value) {
        window.clearTimeout(dragOverlayTimer.value);
        dragOverlayTimer.value = null;
    }

    off(EVENT_TYPES.GO_LOGIN, onGoLogin);
    off(EVENT_TYPES.RELOAD_ROUTER, onReloadRouter);
    off(EVENT_TYPES.LOGIN_SUCCESS, onLoginSuccess);
});
</script>

<style scoped lang="scss">
.test-button {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000;
}

.drag-overlay {
    position: fixed;
    inset: 0;
    background: rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.drag-overlay-content {
    width: 500px;
    max-width: 100%;
    padding: 30px;
    height: 300px;

    img {
        width: 100px;
        margin-bottom: 20px;
    }

    span {
        color: #000000;
        font-weight: 500;
        font-size: 30px;
    }
}

.boot-loading-skeleton-container {
    min-height: calc(100vh - 100px);
    width: 100%;
    box-sizing: border-box;
}
</style>
